import { type PropsWithChildren } from 'react'
import { Box } from '../../primitives/Box'
import { type DataTestIdAttribute } from '../../types'
import { useSpacingToken } from '../../theme/hooks/use-spacing-token'
import { type CardState } from './Card/CardProvider'
import { useCard } from './use-card'

export type CardContentProps = PropsWithChildren<{
  /**
   * You need the complete section
   * This will overrule any other noPadding setting
   */
  noPaddings?: boolean

  /**
   * Special condition where you do not need the spacing to the top
   */
  noPaddingTop?: boolean

  /**
   * Special condition where you do not need the spacing to the bottom
   */
  noPaddingBottom?: boolean

  /**
   * Special condition where you do not need the spacing to the sides
   */
  noPaddingSides?: boolean
}> &
  DataTestIdAttribute & {
    sx?: {
      // make sure we do not give flex minHeight nor paddings for consumers
      alignContent?: string
      alignSelf?: string
    }
  }

const getStyles = ({
  isDense,
  spacingToken,
  noPaddings,
  noPaddingSides,
  noPaddingTop,
  noPaddingBottom,
  sx,
}: Pick<CardState, 'isDense'> & CardContentProps & { spacingToken: number }) => {
  if (noPaddings || (noPaddingSides && noPaddingTop && noPaddingBottom)) {
    return {
      flex: '1',
      minHeight: 0,
      ...sx,
    }
  }

  const px = isDense ? 2 : spacingToken
  const pt = noPaddingTop ? 0 : 1.5
  const pb = noPaddingBottom ? 0 : 2

  return { flex: '1', minHeight: 0, px: noPaddingSides ? 0 : px, pt, pb, ...sx }
}

export const BaseCardContent = ({ children, 'data-testid': testId, ...rest }: CardContentProps) => {
  const spacingToken = useSpacingToken()
  const { isDense } = useCard()
  const sx = getStyles({ isDense, spacingToken, ...rest })

  return (
    <Box sx={sx} data-testid={testId}>
      {children}
    </Box>
  )
}
